import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import type { ApplicationPayload } from "ee/constants/ReduxActionConstants";
import { useDispatch, useSelector } from "react-redux";
import type { AppState } from "ee/reducers";
import {
  getCurrentPageId,
  getViewModePageList,
} from "selectors/editorSelectors";
import { getCurrentWorkspaceId } from "ee/selectors/selectedWorkspaceSelectors";
import { getCurrentUser } from "selectors/usersSelectors";
import type { User } from "constants/userConstants";
import type { Theme } from "constants/DefaultTheme";
import { getThemeDetails, ThemeMode } from "selectors/themeSelectors";
import HtmlTitle from "../AppViewerHtmlTitle";
import { NAVIGATION_SETTINGS } from "constants/AppConstants";
import PageMenu from "pages/AppViewer/PageMenu";
import { useHref } from "pages/Editor/utils";
import { builderURL } from "ee/RouteBuilder";
import TopHeader from "./components/TopHeader";
import Sidebar from "./Sidebar";
import SidebarStacked from "./SidebarStacked";
import _get from "lodash/get";
import {
  getCurrentApplication,
  isMobileLayout,
} from "ee/selectors/applicationSelectors";
import { useIsMobileDevice } from "utils/hooks/useDeviceDetect";
import { setAppViewHeaderHeight } from "actions/appViewActions";

export function Navigation() {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const isEmbed = queryParams.get("embed");
  const embedHideHeader = !!isEmbed;
  const [isMenuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const pageId = useSelector(getCurrentPageId);
  const editorURL = useHref(builderURL, { pageId });
  const currentWorkspaceId: string = useSelector(getCurrentWorkspaceId);
  const currentUser: User | undefined = useSelector(getCurrentUser);
  const lightTheme: Theme = useSelector((state: AppState) =>
    getThemeDetails(state, ThemeMode.LIGHT),
  );
  const currentApplicationDetails: ApplicationPayload | undefined = useSelector(
    getCurrentApplication,
  );
  const pages = useSelector(getViewModePageList);
  const isMobileDevice = useIsMobileDevice();
  const dispatch = useDispatch();
  const isMobileTaroApp = useSelector(isMobileLayout);
  useEffect(() => {
    const header = document.querySelector(".js-appviewer-header");

    dispatch(setAppViewHeaderHeight(header?.clientHeight || 0));

    return () => {
      dispatch(setAppViewHeaderHeight(0));
    };
  }, [
    currentApplicationDetails?.applicationDetail?.navigationSetting?.navStyle,
    currentApplicationDetails?.applicationDetail?.navigationSetting
      ?.orientation,
  ]);

  const navStyle = _get(
    currentApplicationDetails,
    ["applicationDetail", "navigationSetting", "navStyle"],
    "inline",
  ); // 堆叠/行内
  const navOrientation = _get(
    currentApplicationDetails,
    ["applicationDetail", "navigationSetting", "orientation"],
    "top",
  ); // 顶部/侧边

  const currentApp = useSelector(getCurrentApplication);

  const viewerLayout = currentApp?.viewerLayout;
  let isHiddenPage: boolean = false;
  const findPageInTree = (treeData: any[], pageId: string): any | undefined => {
    for (const item of treeData) {
      if (item.isPage && item.pageId === pageId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const result = findPageInTree(item.children, pageId);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  };

  if (viewerLayout) {
    const treeData = _get(JSON.parse(viewerLayout), "treeData", []);
    const currentApp = findPageInTree(treeData, pageId);
    isHiddenPage = _get(currentApp, "isHidden", false);
  }


  const renderNavigation = () => {
    if (isMobileTaroApp || embedHideHeader) {
      return null;
    }
    if (isMobileDevice) {
      return (
        <TopHeader
          currentApplicationDetails={currentApplicationDetails}
          currentUser={currentUser}
          currentWorkspaceId={currentWorkspaceId}
          isMenuOpen={isMenuOpen}
          isMobile={isMobileDevice || isMobileTaroApp}
          pages={pages}
          setMenuOpen={setMenuOpen}
          showUserSettings={!isEmbed}
        />
      );
    }

    if (
      !isMobileDevice &&
      currentApplicationDetails?.applicationDetail?.navigationSetting
        ?.orientation === NAVIGATION_SETTINGS.ORIENTATION.SIDE &&
      !isHiddenPage
    ) {
      return (
        <>
          {/*
           * We need to add top header since we want the current mobile
           * navigation experience until we create the new sidebar for mobile.
           */}
          {navStyle === NAVIGATION_SETTINGS.NAV_STYLE.INLINE ? (
            <Sidebar
              currentApplicationDetails={currentApplicationDetails}
              currentUser={currentUser}
              currentWorkspaceId={currentWorkspaceId}
              pages={pages}
              showUserSettings={!isEmbed}
            />
          ) : (
            <SidebarStacked
              currentApplicationDetails={currentApplicationDetails}
              currentUser={currentUser}
              currentWorkspaceId={currentWorkspaceId}
              pages={pages}
              showUserSettings={!isEmbed}
            />
          )}
        </>
      );
    }

    return (
      <TopHeader
        currentApplicationDetails={currentApplicationDetails}
        currentUser={currentUser}
        currentWorkspaceId={currentWorkspaceId}
        isMenuOpen={isMenuOpen}
        isMobile={isMobileDevice || isMobileTaroApp}
        pages={pages}
        setMenuOpen={setMenuOpen}
        showUserSettings={!isEmbed}
      />
    );
  };

  if (embedHideHeader) return <HtmlTitle />;

  return (
    <ThemeProvider theme={lightTheme}>
      <div ref={headerRef}>
        {/* Since the Backend doesn't have navigationSetting field by default
        and we are creating the default values only when any nav settings via the
        settings pane has changed, we need to hide the navbar ONLY when the showNavbar
        setting is explicitly set to false by the user via the settings pane. */}
        {currentApplicationDetails?.applicationDetail?.navigationSetting
          ?.showNavbar !== false && renderNavigation()}

        <PageMenu
          application={currentApplicationDetails}
          headerRef={headerRef}
          isOpen={isMenuOpen}
          pages={pages}
          setMenuOpen={setMenuOpen}
          url={editorURL}
        />
      </div>
    </ThemeProvider>
  );
}

export default Navigation;
